/**
 * Location change modal (premium feature).
 *
 * Lets users search for a city or address via the Google Maps Geocoding API.
 * On success, triggers a re-fetch of UV data and elevation for the new location.
 * Shows a shake animation and error message when the search fails.
 */
import React, { useState } from 'react';
import './LocationChangeModal.css';

const LocationChangeModal = ({ isOpen, onClose, onLocationChange, uvIndex, isDark }) => {
  const [locationInput, setLocationInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isShaking, setIsShaking] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!locationInput.trim()) return;

    setIsLoading(true);
    setError('');
    setIsShaking(false);

    try {
      const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      if (!API_KEY) {
        throw new Error('Google Maps API key not configured');
      }

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(locationInput)}&key=${API_KEY}`
      );

      const data = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const result = data.results[0];
        const { lat, lng } = result.geometry.location;
        const formattedAddress = result.formatted_address;

        // Call the parent function to update the location
        await onLocationChange(lat, lng, formattedAddress);
        
        // Reset form and close modal
        setLocationInput('');
        onClose();
      } else {
        // Location not found - show error with shake animation
        setError('Location not found. Please try a different search.');
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setError('Unable to search for location. Please try again.');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setLocationInput('');
    setError('');
    setIsShaking(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="location-modal-overlay" onClick={handleClose}>
      <div 
        className={`location-modal ${isShaking ? 'shake' : ''}`} 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
          color: isDark ? '#ffffff' : '#000000'
        }}
      >
        <div className="location-modal-header">
          <h2>Change location</h2>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="location-form">
          <div className="input-group">
            <label htmlFor="locationInput">Enter a city, address, or location:</label>
            <input
              id="locationInput"
              type="text"
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              placeholder="e.g., San Francisco, CA or Times Square, NYC"
              className={`location-input ${error ? 'error' : ''}`}
              disabled={isLoading}
              style={{
                backgroundColor: isDark ? '#404040' : '#ffffff',
                color: isDark ? '#ffffff' : '#000000',
                borderColor: error ? '#ff4444' : (isDark ? '#666666' : '#cccccc')
              }}
            />
            {error && <div className="error-message">{error}</div>}
          </div>
          
          <div className="modal-buttons">
            <button
              type="button"
              onClick={handleClose}
              className="cancel-button"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="search-button"
              disabled={isLoading || !locationInput.trim()}
            >
              {isLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationChangeModal;